import type { CSSProperties, MouseEventHandler, ReactNode } from 'react'
import { formatCreditNumber, formatCredits, type CreditFormatOptions } from './formatCredits'
import type { CreditsScope } from '../../context/creditsContextCore'
import { useNls } from '../../nls/nlsProvider'
import { getSettingsCreditScopeLabel, type SettingsNlsT } from '../Settings/SettingsNls'
import './credits.css'

export type CreditBalanceVariant = 'pill' | 'inline' | 'hero' | 'bare'

export type CreditBalanceProps = {
  value: number | null | undefined
  scope?: CreditsScope
  variant?: CreditBalanceVariant
  label?: string | null
  locale?: CreditFormatOptions['locale']
  suffix?: CreditFormatOptions['suffix']
  className?: string
  style?: CSSProperties
  onClick?: MouseEventHandler<HTMLElement>
  title?: string
  children?: ReactNode
}

const defaultLabelForScope = (scope: CreditsScope | undefined, variant: CreditBalanceVariant, t: SettingsNlsT): string | null => {
  if (variant === 'bare') return null
  return getSettingsCreditScopeLabel(scope, t)
}

/** Renders a credit balance with a consistent format and optional scope label.
 * All credit-display surfaces should route through this component so numbers,
 * separators, and labels stay uniform. */
export const CreditBalance = ({
  value,
  scope,
  variant = 'inline',
  label,
  locale = 'en-US',
  suffix = 'cr',
  className,
  style,
  onClick,
  title,
  children,
}: CreditBalanceProps) => {
  const { t } = useNls()
  const resolvedLabel = label === null ? null : label ?? defaultLabelForScope(scope, variant, t)
  const isBare = variant === 'bare'
  const numeric = isBare ? formatCreditNumber(value, locale) : formatCredits(value, { locale, suffix })

  const baseClass = `credit-balance credit-balance--${variant}${scope ? ` credit-balance--${scope}` : ''}${
    className ? ` ${className}` : ''
  }`

  const interactive = Boolean(onClick)

  return (
    <span
      className={baseClass}
      style={style}
      onClick={onClick}
      title={title}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      {resolvedLabel ? <span className="credit-balance__label">{resolvedLabel}</span> : null}
      <span className="credit-balance__value">{numeric}</span>
      {children}
    </span>
  )
}
